import {
    useCallback,
    useContext,
    useEffect,
    useState,
    createContext,
} from 'react';
import { Howl } from 'howler';
import manifest from '../assets/audio.packer';

export type InitializationOptions = {
    loaded: boolean;
    sound: boolean;
    language: string;
    caller: string;
    announcer: string;
};

export type BingoSounds = {
    ui?: Howl;
    announcer?: Howl;
    ball30?: Howl;
    ball75?: Howl;
    ball80?: Howl;
    ball90?: Howl;
};

type StoppablePromise<T> = Promise<T> & {
    stop(): void;
};

export type SoundPlayer = {
    play: (key: keyof BingoSounds, id: string) => StoppablePromise<void>;
    playLoop: (key: keyof BingoSounds, id: string) => () => void;
    mute: (isMuted: boolean) => void;
    initialized: boolean;
};

const createHowler = async (key: string) => {
    const audioManifest = manifest[key];

    if (!audioManifest) {
        return;
    }

    const response = await fetch(audioManifest.spriteFile);

    const sprite: Record<string, [number, number]> = await response.json();

    return new Howl({
        src: audioManifest.audioFiles,
        sprite: sprite,
        mute: true,
    });
};

const createHowlerWithFallbacks = async (keys: string[]) => {
    for (const key of keys) {
        const howler = await createHowler(key);
        if (howler) {
            return howler;
        }
    }
};

export const SoundContext = createContext<SoundPlayer | null>(null);

export const useSounds = () => useContext(SoundContext) as SoundPlayer;

export const DEFAULT_SOUND_PLAYER: SoundPlayer = {
    play: () => {
        const promise = Promise.resolve() as StoppablePromise<void>;
        promise.stop = () => {};
        return promise;
    },
    playLoop: () => () => {},
    mute: () => {},
    initialized: false,
};

const createLanguageSelection = (language: string) => {
    const languages = [language];
    const [prefix, suffix] = language.split('-');

    if (suffix) {
        languages.push(prefix);
    }

    return [...new Set(languages.concat('en'))];
};

export const useSoundInitialization = ({
    loaded,
    sound,
    language,
    caller,
    announcer,
}: InitializationOptions) => {
    const [sounds, setSounds] = useState<SoundPlayer>(DEFAULT_SOUND_PLAYER);

    useEffect(() => {
        if (!loaded) {
            return;
        }

        let unmounted = false;

        async function initializeSounds() {
            const languages = createLanguageSelection(language);
            const [sUI, sAnnouncer, sBall30, sBall75, sBall80, sBall90] =
                await Promise.all([
                    createHowler('sounds/ui'),
                    createHowlerWithFallbacks(
                        languages.map(
                            (lang) => `speech/${lang}/${announcer}/announcer`
                        )
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/30`)
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/75`)
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/80`)
                    ),
                    createHowlerWithFallbacks(
                        languages.map((lang) => `speech/${lang}/${caller}/90`)
                    ),
                ]);

            const howlers: BingoSounds = {
                ui: sUI,
                announcer: sAnnouncer,
                ball30: sBall30,
                ball75: sBall75,
                ball80: sBall80,
                ball90: sBall90,
            };

            if (unmounted) {
                return;
            }

            setSounds({
                initialized: true,
                play(key, id) {
                    let stop = () => {};

                    const promise = new Promise<void>((resolve) => {
                        const howler = howlers[key];
                        if (!howler) {
                            resolve();
                            return;
                        }

                        const soundId = howler.play(id);
                        howler.on('end', function onEnd($soundId) {
                            if (soundId === $soundId) {
                                resolve();
                                howler.off('end', onEnd);
                            }
                        });

                        stop = () => {
                            howler.stop(soundId);
                        };
                    }) as StoppablePromise<void>;

                    promise.stop = () => {
                        stop();
                    };

                    return promise;
                },
                playLoop(key, id) {
                    const howler = howlers[key];

                    if (!howler) {
                        return () => {};
                    }

                    const soundId = howler.play(id);
                    howler.loop(true, soundId);

                    return () => {
                        howler.loop(false, soundId);
                        howler.stop(soundId);
                    };
                },
                mute(isMuted) {
                    for (const key in howlers) {
                        const howler = howlers[key as keyof BingoSounds];
                        if (howler) {
                            howler.mute(isMuted);
                        }
                    }
                },
            });
        }

        initializeSounds();

        return () => {
            unmounted = true;
        };
    }, [loaded, language, caller, announcer]);

    if (sounds.initialized) {
        sounds.mute(!sound);
    }

    return sounds;
};

export const useSoundHandler = <A, R>(
    callback: ((...args: A[]) => R) | undefined,
    id: string,
    key: keyof BingoSounds = 'ui'
) => {
    const sounds = useSounds();

    return useCallback(
        (...args: A[]) => {
            sounds.play(key, id);
            if (callback) {
                return callback(...args);
            }
        },
        [callback, sounds, key, id]
    );
};
